import React, { useCallback, useMemo, useRef } from 'react';
import { AppButton, Box, ErrorModal, Inscription, InvalidDocumentIcon, Label, Preloader } from '@common-fe/common-fe';
import styled from 'styled-components';

import { allowedExtensions, allowedFileTypes, MAX_SIZE_OF_FILE } from '@/common/constants';
import { WarnModal } from '@/components';
import { isValidFileType } from '@/utils/handlers/isValidFileType';

import PreviewPaymentDocument from '../../TransactionDetails/components/TransactionPaymentDetails/components/PreviewPaymentDocument/PreviewPaymentDocument';

import { UploadExcessContributionFile, useUploadExcessContributionFileQuery } from './useUploadExcessContributionFile.query';

const getFileNameFromUrl = (url?: string) => {
  if (!url) return url;
  try {
    const { pathname } = new URL(url);
    return pathname.split('/').pop() || url;
  } catch {
    return url;
  }
};

const StyledLabel = styled(Label)`
  > div {
    min-width: 424px;
  }
`;

interface Props {
  isEditMode?: boolean;
  fileUrl?: UploadExcessContributionFile;
  setFileUrl?: (data: UploadExcessContributionFile) => void;
  employeeId?: string;
}

export const UploadProofFile: React.FC<Props> = ({ isEditMode, fileUrl, setFileUrl, employeeId }) => {
  const [hasFileRequirementsModal, setHasFileRequirementsModal] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [isDocPreviewModalVisible, setIsDocPreviewModalVisible] = React.useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, isLoading } = useUploadExcessContributionFileQuery(
    (data) => {
      setFileUrl?.(data);
    },
    (errorMessage) => {
      setErrorMessage(errorMessage || '');
    }
  );

  const handleUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !employeeId) return;

      if (file.size > MAX_SIZE_OF_FILE || !isValidFileType(file)) {
        setHasFileRequirementsModal(true);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      try {
        await upload({ employeeId: Number(employeeId), file });
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [upload, employeeId]
  );

  const fileName = useMemo(() => getFileNameFromUrl(fileUrl?.file_url), [fileUrl]);

  return (
    <Box data-testId="UploadProofFile" direction="row" justify="between" align="center">
      {fileUrl ? (
        <Box direction="row">
          <Box width={{ max: '74px' }}>
            <PreviewPaymentDocument
              key={fileUrl?.file_url}
              paymentDocumentPath={fileUrl?.file_url || ''}
              modalTitle="File Preview"
              isNumberOfImageHidden
              isDocPreviewModalVisible={isDocPreviewModalVisible}
              setIsDocPreviewModalVisible={setIsDocPreviewModalVisible}
            />
          </Box>
          <Box margin={{ left: 'spacing12' }} justify="center">
            <Inscription>
              {fileName}
            </Inscription>
          </Box>
        </Box>
      ) : (
        <StyledLabel showRequireIcon isNarrow size="14px">
          Provide proof of the participant’s withdrawal request
          <Box margin={{ left: '-10px', top: 'spacing2' }} pad={{ right: 'spacing4' }}>
            <Inscription size="small" weight="normal" lineHeight="18px" color="textSecondary">
              PDF, HEIF, JPEG, JPG, PNG, TIFF
            </Inscription>
          </Box>
        </StyledLabel>
      )}

      {isEditMode ? (
        <>
          <Box>
            {isLoading ? (
              <Preloader testId="upload-proof-file-preloader" size={14} thinSize={3} />
            ) : (
              <Inscription
                data-testid="upload-proof-file-action"
                color="textAccent"
                onClick={handleUpload}
                style={{ cursor: 'pointer' }}
              >
                {fileUrl ? 'Change' : 'Upload'}
              </Inscription>
            )}
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept={`${allowedExtensions.join(',')},${allowedFileTypes.join(',')}`}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            disabled={isLoading}
          />
        </>
      ) : (
        <Box>
          <Inscription
            data-testid="upload-proof-file-preview"
            color="textAccent"
            onClick={() => setIsDocPreviewModalVisible(true)}
            style={{ cursor: 'pointer' }}
          >
            Preview
          </Inscription>
        </Box>
      )}

      <WarnModal
        testId="upload-proof-file-requirements-warn-modal"
        header="We can’t upload your document"
        iconNode={<InvalidDocumentIcon />}
        helptext="The document you uploaded doesn’t meet our requirements. Please convert the file into an approved file type such as: PDF, HEIF, JPEG, JPG, PNG, TIFF and file size up to 20MB."
        submitButtonNode={(
          <AppButton
            testId="uploadAnotherDocument"
            width="200px"
            onClick={() => {
              setHasFileRequirementsModal(false);
              handleUpload();
            }}
          >
            Upload Another Document
          </AppButton>
        )}
        cancelButtonNode={(
          <AppButton
            testId="closeFileRequirementsModal"
            buttonType="secondary"
            width="200px"
            onClick={() => {
              setHasFileRequirementsModal(false);
            }}
          >
            Close
          </AppButton>
        )}
        visible={hasFileRequirementsModal}
        onSetVisible={() => {
          setHasFileRequirementsModal(false);
        }}
      />

      <ErrorModal
        testId="upload-proof-file-error-modal"
        header="Error"
        helpText={errorMessage}
        visible={Boolean(errorMessage)}
        onSetVisible={() => {
          setErrorMessage('');
        }}
        buttonText="Close"
      />
    </Box>
  );
};
